import React, { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProfileImage from '../profileImage';
import { usePostComments } from '../../hooks/useFeed';

const CommentSection = ({
  post,
  currentUser,
  activeCommentPost,
  comment,
  setComment,
  onComment,
  isCommenting
}) => {
  const commentsContainerRef = useRef(null);
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    const postId = post.postId || post._id || post.id;
    if (comment.trim() && postId) {
      onComment(postId);
    }
  };

  const postId = post.postId || post._id || post.id;
  const { 
    data: commentsData, 
    isLoading: commentsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = usePostComments(activeCommentPost === postId ? postId : null);

  // Flatten infinite query data
  const allComments = commentsData?.pages?.flatMap(page => page.comments) || [];
  const totalComments = commentsData?.pages?.[0]?.totalCount || 0;

  // Handle scroll to load more comments
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  
  if (activeCommentPost !== postId) return null;

  return (
    <div className="absolute top-full left-0 right-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg mt-2 p-4">
      <style jsx>{`
        .comments-container::-webkit-scrollbar {
          width: 6px;
        }
        .comments-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .comments-container::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }
        .comments-container::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>
      {/* Comment Form */}
      <form onSubmit={handleCommentSubmit} className="mb-4">
        <div className="flex gap-3">
          <ProfileImage
            image={currentUser?.profilePicture}
            alt={currentUser?.userName}
            className="w-8 h-8 rounded-full object-cover"
          />
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              placeholder="Write a comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={!comment.trim() || isCommenting}
              className="px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all font-medium"
            >
              {isCommenting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              ) : (
                'Post'
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Comments Display */}
      {commentsLoading && allComments.length === 0 ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="text-gray-500 text-sm mt-2">Loading comments...</p>
        </div>
      ) : allComments.length > 0 ? (
        <div 
          ref={commentsContainerRef}
          className={`space-y-3 comments-container ${totalComments > 3 ? 'max-h-80 overflow-y-auto pr-2' : ''}`}
          onScroll={totalComments > 3 ? handleScroll : undefined}
        >
          {allComments.map((comment, index) => {
            const isOptimistic = comment._id?.startsWith('temp-');
            return (
              <div 
                key={comment._id || comment.id || `comment-${index}`} 
                className={`flex gap-3 rounded-lg p-3 ${
                  isOptimistic 
                    ? 'bg-blue-50 border border-blue-200' 
                    : 'bg-gray-50'
                                 }`}
               >
                 <Link to={`/feed/${comment.user?._id || comment.user?.id}`}>
                   <ProfileImage
                     image={comment.user?.profilePicture}
                     alt={comment.user?.userName}
                     className="w-8 h-8 rounded-full object-cover flex-shrink-0 hover:ring-2 hover:ring-blue-200 transition-all"
                   />
                 </Link>
                 <div className="flex-1 min-w-0">
                   <div className="flex items-center gap-2 mb-1">
                     <Link 
                       to={`/feed/${comment.user?._id || comment.user?.id}`}
                       className="font-medium text-sm text-gray-900 truncate hover:text-blue-600 transition-colors"
                     >
                       {comment.user?.userName}
                     </Link>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {comment.timeAgo}
                    </span>
                    {isOptimistic && (
                      <span className="text-xs text-blue-500 flex-shrink-0">
                        Posting...
                      </span>
                    )}
                  </div>
                  <p className={`text-sm break-words ${
                    isOptimistic ? 'text-blue-700' : 'text-gray-700'
                  }`}>
                    {comment.content}
                  </p>
                </div>
              </div>
            );
          })}
          
          {/* Loading more indicator */}
          {isFetchingNextPage && (
            <div className="text-center py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent mx-auto"></div>
              <p className="text-gray-500 text-xs mt-1">Loading more comments...</p>
            </div>
          )}
          
          {/* Show total count if there are more than 3 comments */}
          {totalComments > 3 && (
            <div className="text-center py-2 border-t border-gray-100">
              <p className="text-gray-500 text-xs">
                Showing {allComments.length} of {totalComments} comments
                {hasNextPage && ' - Scroll down to load more'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-gray-500 text-sm">No comments yet. Be the first to comment!</p>
        </div>
      )}
    </div>
  );
};

export default CommentSection; 